<template>
  <div class="page" :style="themeStyle">
    <div v-if="!loaded" class="loading">Loading…</div>
    <div v-if="loaded" class="root">

      <!-- Top nav -->
      <nav class="nav">
        <div class="nav-left">
          <img :src="navLogoUrl" class="nav-logo" />
        </div>
        <div class="nav-partnership">
          <div class="np-lo">{{ loName || "Loan Officer" }}</div>
          <div class="np-amp">&amp;</div>
          <div class="np-re">{{ realtorName || realtor?.partner_title || 'Partner' }}</div>
        </div>
  
      </nav>

      <!-- HERO: diagonal cut with overlapping cards -->
      <div class="hero-wrapper">

        <!-- Warm hero section -->
        <section class="hero-section">
          <div class="hero-content">
     
            <h1 class="hero-h1" v-html="heroH1Html"></h1>
            <p class="hero-body">{{ heroBody }}</p>
            <div class="hero-btns">
              <a class="btn-primary" :href="applyRightLink">{{ heroPrimaryText }}</a>
              <a v-if="applyLeftLink" class="btn-ghost" :href="applyLeftLink">{{ heroSecondaryText }}</a>
            </div>
            <div class="hero-contacts">
              <a v-if="loinfo && loinfo.phone_number" class="hc-item" :href="'tel:' + loinfo.phone_number">Call us</a>
              <span class="hc-sep">·</span>
              <a v-if="loinfo && loinfo.email" class="hc-item" :href="'mailto:' + loinfo.email">Email us</a>
              <span v-if="realtor && realtor.website_url" class="hc-sep">·</span>
              <a v-if="realtor && realtor.website_url" class="hc-item" :href="realtor.website_url" target="_blank" rel="noreferrer">Partner site</a>
            </div>
          </div>

          <!-- Diagonal cut bottom -->
          <div class="hero-cut"></div>
        </section>

        <!-- Cards that overlap the diagonal cut -->
        <div class="overlap-cards">

          <div class="ocard lo-ocard">
            <div class="ocard-pip"></div>
            <div class="ocard-header">
              <img v-if="loinfo && loinfo.lo_image" :src="loinfo.lo_image" class="ocard-img" />
              <div>
                <div class="ocard-badge">Loan Officer</div>
                <div class="ocard-name">{{ loName }}</div>
                <div class="ocard-role">{{ loTitle }}</div>
                <div v-if="loinfo && loinfo.nmls" class="ocard-nmls">NMLS {{ (loinfo.nmls || "").trim() }}</div>
              </div>
            </div>
            <div class="ocard-body">
              <a v-if="loinfo && loinfo.phone_number" :href="'tel:' + loinfo.phone_number" class="ocard-chip">{{ loinfo.phone_number }}</a>
              <a v-if="loinfo && loinfo.email" :href="'mailto:' + loinfo.email" class="ocard-chip">{{ loinfo.email }}</a>
            </div>
        
          </div>

          <div class="ocard re-ocard">
            <div class="ocard-pip re-pip"></div>
            <div class="ocard-header">
              <img v-if="realtor && realtor.headshot_url" :src="realtor.headshot_url" class="ocard-img" />
              <div>
                <div class="ocard-badge re-badge">{{ realtor?.partner_title || 'Partner' }}</div>
                <div class="ocard-name">{{ realtorName }}</div>
                <div class="ocard-role">{{ realtor && realtor.brokerage_name ? realtor.brokerage_name : "" }}</div>
                <div v-if="realtor && realtor.nmls" class="ocard-nmls">NMLS {{ (realtor.nmls || "").trim() }}</div>
              </div>
            </div>
            <div class="ocard-body">
              <a v-if="realtor && realtor.phone_number" :href="'tel:' + realtor.phone_number" class="ocard-chip re-chip">{{ realtor.phone_number }}</a>
              <a v-if="realtor && realtor.email" :href="'mailto:' + realtor.email" class="ocard-chip re-chip">{{ realtor.email }}</a>
              <a v-if="realtor && realtor.website_url" :href="realtor.website_url" class="ocard-chip re-chip" target="_blank" rel="noreferrer">Website →</a>
            </div>
          </div>

        </div>
      </div>

      <!-- Light lower section -->
      <section class="lower-section"></section>

    </div>
  </div>
</template>

<script>
import coBrandMixin from "../../mixins/coBrandMixin.js";

const BRAND_COLORS = {
  zonalending:        { primary: "#eb610a", accent: "#646363" },
  radiantmortgage:    { primary: "#1C1F39", accent: "#DB9457" },
  conradmortgage:     { primary: "#4a4b4b", accent: "#d4af37" },
  theimgteam:         { primary: "#ff6633", accent: "#4a4c54" },
  responsivemortgage: { primary: "#07212c", accent: "#FF7F47" },
  kpmortgage:         { primary: "#0D2821", accent: "#34BFC6" },
  ignitemortgage:     { primary: "#000000", accent: "#00b1eb" },
  goldstarfinancial:  { primary: "#0b1726", accent: "#c69c39" },
};

const DBA_BASE = "https://api.goldstarfinancial.com/DBA";
function dbaLogo(brandKey, variant = "primary") {
  return `${DBA_BASE}/${brandKey}/${variant}.png`;
}
function getBrandKey(email) {
  if (!email) return null;
  const d = email.slice(email.indexOf("@") + 1).toLowerCase();
  return d.split(".")[0] || null;
}

export default {
  name: "CoBrandTerracotta",
  mixins: [coBrandMixin],
  computed: {
    brandKey() {
      const k = getBrandKey(this.loinfo?.email);
      return (k && BRAND_COLORS[k]) ? k : "goldstarfinancial";
    },
    palette() {
      return BRAND_COLORS[this.brandKey] || BRAND_COLORS.goldstarfinancial;
    },
    themeStyle() {
      return { "--p": this.palette.primary, "--a": this.palette.accent };
    },
    // Nav sits on dark hero-bg — use the white/light DBA logo variant
    navLogoUrl() {
      return dbaLogo(this.brandKey, "primary");
    },
    
    heroH1Html() {
      const h = (this.realtor?.header1 || this.loinfo?.header1 || "").trim();
      if (!h) return "Ready to get<br/><em>started?</em>";
      const parts = h.split(",");
      if (parts.length > 1) {
        const a = parts[0].trim();
        const b = parts.slice(1).join(",").trim();
        return `${a}<br/><em>${b}</em>`;
      }
      return h;
    },
    
    heroHeading()     { return this.loinfo?.text_section_heading || "About"; },
    heroPrimaryText() { return this.loinfo?.right_button_text || "Apply Now"; },
    heroSecondaryText(){ return this.loinfo?.left_button_text || "Schedule Appointment"; },
    heroBody() {
      const m = (this.realtor?.main || this.loinfo?.main || "").trim();
      if (m) return m;
      const p = [this.loinfo?.text_section_body_p1, this.loinfo?.text_section_body_p2].filter(Boolean).join(" ").trim();
      return p || this.loinfo?.footer_slogan || "Start your application in minutes and we'll guide you from application through closing with a personal touch.";
    },
  },
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Libre+Baskerville:ital,wght@0,400;0,700;1,400&family=Nunito:wght@300;400;500;600&display=swap');

.page {
  min-height: 100vh;
  background: #f7f4ef;
  color: #2d2319;
  font-family: 'Nunito', sans-serif;
  --p: #0b1726;
  --a: #c69c39;
  --terra: color-mix(in srgb, var(--a) 30%, #d4713a);
  --hero-bg: color-mix(in srgb, var(--p) 94%, var(--terra));
  --hero-text: #f7f0e6;
  --card-bg: #ffffff;
  --hairline: rgba(45, 35, 25, 0.10);
  --muted: #7d6b5a;
  --a-tint: color-mix(in srgb, var(--a) 12%, #f7f4ef);
  --p-tint: color-mix(in srgb, var(--p) 6%, #f7f4ef);
}

.root { display: flex; flex-direction: column; min-height: 100vh; }

/* ── NAV ─────────────────────────────────────────────────────────────────── */
.nav {
  display: flex; align-items: center; justify-content: space-between;
  padding: 18px 44px;
  background: var(--hero-bg);
  position: relative; z-index: 20;
}
.nav-logo { width: 160px; max-height:80px;  object-fit: contain;  filter: brightness(0) invert(1); opacity: 0.9; } 

.nav-partnership { text-align: center; }
.np-lo  { font-size: 14px; font-weight: 600; color: var(--hero-text); }
.np-amp { font-size: 11px; color: color-mix(in srgb, var(--a) 70%, var(--hero-text)); margin: 2px 0; }
.np-re  { font-size: 13px; color: color-mix(in srgb, var(--hero-text) 60%, transparent); }
.nav-cta {
  font-size: 13px; font-weight: 600; text-decoration: none;
  background: var(--a); color: var(--p);
  padding: 10px 22px; border-radius: 50px;
  transition: opacity 0.2s;
}
.nav-cta:hover { opacity: 0.88; }

/* ── HERO ────────────────────────────────────────────────────────────────── */
.hero-wrapper { position: relative; }

.hero-section {
  background: var(--hero-bg);
  padding: 60px 44px 160px;
  position: relative;
  overflow: hidden;
  justify-content: center;
  display: flex;
}
.hero-section::before {
  content: "";
  position: absolute; inset: 0;
  background:
    radial-gradient(ellipse 80% 60% at 80% 20%, color-mix(in srgb, var(--terra) 15%, transparent), transparent),
    radial-gradient(ellipse 60% 80% at 10% 80%, color-mix(in srgb, var(--a) 10%, transparent), transparent);
  pointer-events: none;
}

.hero-cut {
  position: absolute;
  bottom: -2px; left: 0; right: 0;
  height: 90px;
  background: #f7f4ef;
  clip-path: polygon(0 100%, 100% 0, 100% 100%);
}
.hero-body{
 text-align: center;
}
.hero-content { position: relative; z-index: 2; max-width: 640px; }

.hero-eyebrow {
  display: block; font-size: 11px; font-weight: 600;
  letter-spacing: 0.22em; text-transform: uppercase;
  color: var(--a); margin-bottom: 14px;
}
.hero-h1 {
  font-family: 'Libre Baskerville', serif;
  font-size: clamp(36px, 5vw, 64px);
  font-weight: 700; line-height: 1.1;
  color: var(--hero-text); margin: 0 0 18px;
}
.hero-h1 em { font-style: italic; font-weight: 400; color: color-mix(in srgb, var(--a) 85%, var(--hero-text)); }
.hero-body {
  font-size: 15px; line-height: 1.75;
  color: color-mix(in srgb, var(--hero-text) 65%, transparent);
   margin-bottom: 28px;
}
.hero-btns { display: flex; gap: 10px; flex-wrap: wrap; margin-bottom: 20px; justify-content: center;}
.btn-primary {
  font-size: 14px; font-weight: 600; text-decoration: none;
  background: var(--a); color: var(--p);
  padding: 13px 28px; border-radius: 50px;
  box-shadow: 0 4px 20px color-mix(in srgb, var(--a) 35%, transparent);
  transition: transform 0.18s, box-shadow 0.18s;
  width:55%
}
.btn-primary:hover { transform: translateY(-1px); box-shadow: 0 8px 28px color-mix(in srgb, var(--a) 45%, transparent); }
.btn-ghost {
  font-size: 14px; font-weight: 500; text-decoration: none;
  color: var(--hero-text); background: rgba(255,255,255,0.1);
  border: 1px solid rgba(255,255,255,0.22);
  padding: 13px 28px; border-radius: 50px;
  transition: background 0.18s;
}
.btn-ghost:hover { background: rgba(255,255,255,0.18); }
.hero-contacts { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.hc-item { font-size: 13px; text-decoration: none; color: color-mix(in srgb, var(--hero-text) 55%, transparent); transition: color 0.18s; }
.hc-item:hover { color: var(--a); }
.hc-sep { color: color-mix(in srgb, var(--hero-text) 25%, transparent); }

/* ── OVERLAP CARDS ───────────────────────────────────────────────────────── */
.overlap-cards {
  position: relative;
  z-index: 10;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  max-width: 840px;
  margin-top: -150px;
  padding: 0 44px;
}

.ocard {
  background: var(--card-bg);
  border-radius: 20px;
  padding: 22px;
  box-shadow: 0 20px 50px rgba(45,35,25,0.15), 0 4px 14px rgba(45,35,25,0.08);
  border: 1px solid var(--hairline);
  position: relative;
  overflow: hidden;
  transition: box-shadow 0.22s, transform 0.22s;
}
.ocard:hover { box-shadow: 0 28px 60px rgba(45,35,25,0.20); transform: translateY(-2px); }

.ocard-pip {
  position: absolute; top: 0; left: 0; right: 0; height: 4px;
  background: linear-gradient(90deg, var(--p), color-mix(in srgb, var(--p) 40%, var(--a)));
  clip-path: polygon(0 0, 100% 0, 97% 100%, 0 100%);
}
.re-pip { background: linear-gradient(90deg, var(--a), color-mix(in srgb, var(--a) 45%, var(--terra))); }

.ocard-header { display: flex; gap: 14px; align-items: flex-start; margin-bottom: 14px; margin-top: 6px; }
.ocard-img { width: 60px; height: 60px; border-radius: 14px; object-fit: cover; }
.ocard-badge {
  display: inline-block; font-size: 9px; font-weight: 600;
  letter-spacing: 0.2em; text-transform: uppercase;
  color: color-mix(in srgb, var(--p) 60%, var(--muted));
  margin-bottom: 5px;
}
.re-badge { color: color-mix(in srgb, var(--a) 80%, var(--p)); }
.ocard-name { font-family: 'Libre Baskerville', serif; font-size: 19px; font-weight: 700; color: var(--p); line-height: 1.2; }
.ocard-role { font-size: 12px; color: var(--muted); margin-top: 3px; }
.ocard-nmls { font-size: 11px; color: color-mix(in srgb, var(--muted) 65%, transparent); margin-top: 2px; }

.ocard-body { display: flex; flex-wrap: wrap; gap: 6px; margin-bottom: 14px; }
.ocard-chip {
  font-size: 11px; font-weight: 500; text-decoration: none;
  background: var(--p-tint); color: var(--p);
  padding: 5px 12px; border-radius: 50px;
  border: 1px solid var(--hairline);
  transition: background 0.18s;
}
.ocard-chip:hover { background: color-mix(in srgb, var(--p) 10%, var(--p-tint)); }
.re-chip {
  background: var(--a-tint); color: color-mix(in srgb, var(--a) 85%, var(--p));
  border-color: color-mix(in srgb, var(--a) 15%, transparent);
}
.re-chip:hover { background: color-mix(in srgb, var(--a) 16%, #f7f4ef); }

.ocard-btn {
  display: block; text-align: center; text-decoration: none;
  background: var(--p); color: #fff;
  font-size: 13px; font-weight: 600; padding: 11px;
  border-radius: 12px; transition: opacity 0.18s;
 
}
.ocard-btn:hover { opacity: 0.87; }

.lower-section { flex: 1; background: #f7f4ef; min-height: 80px; }

@media (max-width: 700px) {
  .overlap-cards { grid-template-columns: 1fr; margin-top: -130px; padding: 0 20px; }
  .hero-section { padding: 44px 24px 100px; }
  .nav { padding: 16px 24px; }
  .nav-partnership { display: none; }
}
</style>